import { Box, Chip, CircularProgress, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { IColorTokens } from "../../../../types/theme";
import { fetchTriggerVariables } from "../services/notificationTemplates";

interface VariableChipsProps {
  triggerSource: string;
  onInsert: (variable: string) => void;
  colors: IColorTokens;
  label?: string;
}

export default function VariableChips({ triggerSource, onInsert, colors, label = "Insert variable" }: VariableChipsProps) {
  const [variables, setVariables] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!triggerSource) {
      setVariables([]);
      return;
    }
    setLoading(true);
    fetchTriggerVariables(triggerSource)
      .then(setVariables)
      .catch(() => setVariables([]))
      .finally(() => setLoading(false));
  }, [triggerSource]);

  if (!triggerSource) return null;

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
        <CircularProgress size={14} sx={{ color: colors.primary[300] }} />
        <Typography variant="caption" sx={{ color: colors.primary[300] }}>
          Loading variables...
        </Typography>
      </Box>
    );
  }

  if (variables.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="caption" sx={{ color: colors.primary[300], display: "block", mb: 0.75 }}>
        {label}:
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
        {variables.map((variable) => (
          <Chip
            key={variable}
            label={variable}
            size="small"
            onClick={() => onInsert(variable)}
            sx={{
              cursor: "pointer",
              bgcolor: colors.primary[800],
              color: colors.primary[300],
              fontFamily: "monospace",
              fontSize: "0.75rem",
              "&:hover": { bgcolor: colors.primary[700], color: "#fff" },
            }}
          />
        ))}
      </Box>
    </Box>
  );
}
